import { useMemo } from 'react'
import { FullTextToolTip } from 'ui_kit_qmd'
import './VisitCard.scss'
import moment from 'moment-timezone'
import { findIana } from 'windows-iana'
import { allTimezones, useTimezoneSelect } from 'react-timezone-select'
import stick from '../../../../images/common/stick.png'
import { text } from './Constants'
import upIcon from '../../../../images/common/ChevronUp.png'
import { getTimeZones, getVisitStatus, parseUserDateTime } from '../../../../utils/common'
import { MAT_MOUD_TEXT } from '../../../../utils/constants'
import { DEFAULT_DATE_FORMAT_WITHOUT_TIME } from '../../../../utils/dateUtils'
import { getVisitMethodIconProps } from '../../../new-visit/components/visit-information/getVisitMethodIconProps'
import { getVisitTypeLabel } from '../../../../utils/getVisitTypeLabel'
import { UNASSIGNED_PROVIDER_TEXT } from '../../../visits/constants'
import { ProgramVisitBadge } from '@/components/program-visit-badge/ProgramVisitBadge'
import { readVisitCoverage } from '@quickmd-llc/frontend-utils'

// @ts-expect-error TS2351 TEMP ERROR SUPPRESSION: CRA -> VITE
export const VisitCard = ({ id, visit, showStatus, clickArrowAction, userTimeZone }) => {
  const timezones = getTimeZones(allTimezones)
  const { parseTimezone } = useTimezoneSelect({ timezones, labelStyle: 'abbrOnly' })

  const patientVisitTime = useMemo(() => {
    if (!visit?.TimeZone || !visit.VisitDateTime) {
      return text.UNAVAILABLE
    }

    const { VisitDateTime, TimeZone } = visit

    const iana = TimeZone ? findIana(visit.TimeZone)?.[0] : null

    if (!iana) {
      return text.UNAVAILABLE
    }

    const visitDateTime = moment.utc(VisitDateTime).tz(iana)

    try {
      return (
        visitDateTime.format('MMM DD YYYY, h:mma ') +
        (parseTimezone(iana, visitDateTime.format(DEFAULT_DATE_FORMAT_WITHOUT_TIME), visitDateTime.format('h:mma'))
          ?.label ?? text.UNAVAILABLE)
      )
    } catch {
      return visitDateTime.format('MMM DD YYYY, h:mma ') + text.UNAVAILABLE
    }
  }, [visit, parseTimezone])

  // Every field on this card is read off `visit`, so there is nothing to draw without one.
  // Render nothing instead of taking the surrounding chart section down with a TypeError.
  if (!visit) {
    return null
  }

  const provider =
    visit.Provider && Object.keys(visit.Provider).length ?
      `${visit.Provider.Prefix ? `${visit.Provider.Prefix} ` : ''}${visit.Provider.FirstName} ${
        visit.Provider.LastName
      }${visit.Provider.Suffix ? ` ${visit.Provider.Suffix}` : ''}`
    : UNASSIGNED_PROVIDER_TEXT

  const toolTipStyle = {
    left: '0px',
    right: '0px',
    width: 'max-content',
    maxWidth: '90%',
    wordBreak: 'break-all',
    margin: 'auto',
    zIndex: 900000,
    pointerEvents: 'none',
  }

  const adminVisitTime = parseUserDateTime(visit.VisitDateTime, userTimeZone, true)

  return (
    <div className='visit-card-container' id={id}>
      <div className='visit-card-header-wrapper'>
        <FullTextToolTip
          id={`${id}-provider`}
          fullText={provider}
          extraStyle={{
            bottom: 55,
            ...toolTipStyle,
          }}
          view={
            <span id={`${id}-provider`} className='visit-card-provider-name bold'>
              <div />
              {provider}
            </span>
          }
        />
        {/* The provider name is `width: calc(100% - 16px)` with an ellipsis, so it
            shrinks to make room for the chip rather than pushing the chevron off. */}
        <ProgramVisitBadge className='shrink-0 self-start' coverage={readVisitCoverage(visit)} />
        {clickArrowAction ?
          <img
            role='presentation'
            id='arrowRight'
            className='visit-card-arrow-right-icon'
            src={upIcon}
            alt={text.DIVIDER}
            onClick={() => {
              clickArrowAction({ visitId: visit.VisitId, provider: visit.Provider })
            }}
          />
        : null}
      </div>
      <FullTextToolTip
        id={`${id}-date`}
        fullText={`${adminVisitTime} (${text.PATIENT} ${patientVisitTime})`}
        extraStyle={{
          bottom: 50,
          ...toolTipStyle,
        }}
        view={
          <span id={`${id}-date`} className='visit-card-name-label'>
            <div />
            {adminVisitTime} ({text.PATIENT} {patientVisitTime})
          </span>
        }
      />

      <div className='visit-card-row-container'>
        {/* eslint-disable-next-line jsx-a11y/alt-text -- alt is provided by getVisitMethodIconProps */}
        <img {...(visit?.Method && getVisitMethodIconProps(visit.Method))} className='visit-card-type-icon' />
        <FullTextToolTip
          id={`${id}-service`}
          fullText={visit.ServiceTypeName}
          extraStyle={{
            bottom: 15,
            ...toolTipStyle,
          }}
          view={
            <div className='visit-card-text-container'>
              <span id={`${id}-service`} className='visit-card-name-label'>
                <div />
                {visit.ServiceTypeName}
              </span>
            </div>
          }
        />
        <img src={stick} alt={text.DIVIDER} className='visit-card-stick-icon' id='stick' />
        <FullTextToolTip
          id={`${id}-visit-type`}
          fullText={getVisitTypeLabel(visit.VisitTypeName, visit.Method)}
          extraStyle={{
            bottom: 15,
            ...toolTipStyle,
          }}
          view={
            <div className='visit-card-text-container'>
              <span id={`${id}-visit-type`} className='visit-card-name-label'>
                <div />
                {getVisitTypeLabel(visit.VisitTypeName, visit.Method)}
              </span>
            </div>
          }
        />
        {visit.IsMedicationAssistedTreatment ?
          <>
            <img src={stick} alt={text.DIVIDER} className='visit-card-stick-icon' id='stick' />
            <div className='visit-card-text-container'>
              <span id={`${id}-visit-type`} className='visit-card-name-label'>
                <div />
                {MAT_MOUD_TEXT}
              </span>
            </div>
          </>
        : null}
      </div>
      {showStatus ?
        <FullTextToolTip
          id={`${id}-status`}
          fullText={getVisitStatus(visit?.Status)}
          extraStyle={{
            bottom: 15,
            ...toolTipStyle,
          }}
          view={
            <div className='visit-card-status-container'>
              <span id={`${id}-status`} className='visit-card-name-label'>
                <div />
                Status: {getVisitStatus(visit?.Status)}
              </span>
            </div>
          }
        />
      : null}
    </div>
  )
}
